import { Button, Checkbox, DatePicker, Form, Input, Select } from "antd";
import TextArea from "antd/es/input/TextArea";
import { FormInstance } from "antd/lib/form";
import React, { useEffect, useRef } from 'react';
import { useSelector } from "react-redux";
import { datetime } from "../../../../../util/library/datetime";
import { selectTenantId } from "../../../../../util/store/selectors";
import { getStore } from "../../../../../util/store/store";
import {
    ACTION_TYPE,
    aHourMillisecond,
    eventTypes,
    OP_STATUS,
    OPERATION_TYPE,
    propContainer,
    SHORT_DATETIME_FORMAT
} from "../../../../../util/varibles/constants";
import { renderOpts, uuid } from "../../../../../util/varibles/global";
import { IOperation } from "../../../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../../../util/varibles/message";
import { Rule } from "../../../../../util/varibles/validation";
import { selectPlan } from "../../../constants";
import { planValidation, VALIDATION, VALIDATION_STATUS } from "../../../util/validation";
import { disabledDate, layout } from "../../Vessels/constants";
import AddChecklist from "../_components/AddChecklist";
import Certain from "../_components/Certain";
import Location from "../_components/Location";
import { getEditOp, useEditOp } from "../EditOperationContext";
import styles from "./style.module.scss";

const { RangePicker } = DatePicker;

const Event: React.FC = () => {
    const formRef = useRef<FormInstance>(null);
    const userTenantId = useSelector(selectTenantId);
    const get = getEditOp();
    const [operation, set] = useEditOp(state => state.operation);
    const [loadingSubmit] = useEditOp(state => state.loadingSubmit);

    useEffect(() => {
        const { activity_log, operation } = get();
        const {
            event_type: eventType = null,
            note = "",
            parallel,
            pois = [],
        } = operation || {};

        const [start, finish] = activity_log;
        const time: any = [];

        let isDuration = 0;
        if (start && start.est_start_time) {
            isDuration = 1;
            time.push(datetime(start.est_start_time).value);
        }
        if (finish && finish.est_start_time) {
            isDuration = 2;
            time.push(datetime(finish.est_start_time).value);
        }
        const duration = isDuration === 2 ? (finish.est_start_time - start.est_start_time) / aHourMillisecond : null

        setTimeout(() => {
            formRef.current?.setFieldsValue({ time, eventType, note, duration, parallel, pois });
        }, 10)
    }, [get, set]);

    if (!operation)
        return null;

    const isPastDateDisabled = [OP_STATUS.PROCESSING, OP_STATUS.FINISHED].includes(operation.status);

    const handleSubmit = (form: any) => {
        const { id = uuid(), available_time_id = '-', contract_id = '-' } = operation || { id: null }
        const { time, eventType, note = '', parallel = false, pois = [] } = form;
        const { vessel, editMode, onSave } = get();

        if (!time[0] || !time[1]) {
            notify.error(NotifyCode.E29)();
            return;
        }

        if (!vessel)
            return;

        const start = datetime(time[0]).time;
        const finish = datetime(time[1]).time;
        const indexId = selectPlan.indexId(getStore());
        const { operation_code = `${indexId}` } = operation;

        const data = {
            vessel,
            operation: {
                ...operation,
                operation_code,
                id,
                est_start_time: start,
                est_finish_time: finish,
                operation_type: OPERATION_TYPE.EVENT,
                event_type: eventType,
                tenant_id: userTenantId,
                note,
                available_time_id,
                contract_id,
                parallel,
                vessel_id: vessel.id
            },
            activity_log: [
                { est_start_time: start },
                { est_start_time: finish }
            ]
        } as IOperation;

        if (pois?.length > 0) {
            data.operation.pois = pois;
        } else {
            delete data.operation.pois;
        }

        onSave(editMode === ACTION_TYPE.CREATE
            ? { add: { [id]: data }, isFocus: id }
            : { update: { [id]: data } });
    }

    const handleChangeTime = (value: any) => {
        const [startTime, finishTime] = value;
        const duration = Math.floor(((finishTime - startTime) / (3600 * 1000) * 100)) / 100;
        if (formRef.current)
            formRef.current.setFieldsValue({ duration })
    }

    const handleChangeDuration = (e: any) => {
        if (!formRef.current)
            return;
        const { value } = e.target
        const duration = Number(value) * 3600 * 1000;
        const time = formRef.current.getFieldValue('time');
        if (time[0] === undefined) return;
        time[1] = datetime(time[0].valueOf() + duration).value;
        formRef.current.setFieldsValue({ time: [...time] });
    }

    const validateTime = (_: any, time: any[] = []) => {
        const { operations, level, vessel } = get();
        const parallel = formRef.current?.getFieldValue('parallel');
        if (parallel || !vessel || !time[0] || !time[1])
            return Promise.resolve();

        const data = {
            vessel,
            operation: {
                ...operation,
                operation_type: OPERATION_TYPE.EVENT,
                est_start_time: datetime(time[0]).time,
                est_finish_time: datetime(time[1]).time,
                parallel: false,
            },
            activity_log: [
                { est_start_time: datetime(time[0]).time },
                { est_start_time: datetime(time[1]).time },
            ],
        } as IOperation;
        const result = planValidation[VALIDATION.TIME](data, { operations, level });

        return result.status === VALIDATION_STATUS.ERROR
            ? Promise.reject(new Error(result.message))
            : Promise.resolve();
    }

    return <Form {...{
        ...layout,
        className: styles['container-event'],
        ref: formRef,
        name: 'event',
        layout: 'vertical',
        onFinish: handleSubmit
    }}>
        <div className={styles['input-group']}>
            <div data-lev='label'>Certainty</div>
            <Certain
                className='w-full'
                isLabel={false}
                value={operation?.certain_mode}
                onChange={(certain_mode: any) => set(({ operation: prev }) => prev ? {
                    operation: {
                        ...prev,
                        certain_mode
                    }
                } : {})}
            />
        </div>
        <Form.Item label="Type" name='eventType' rules={[Rule.Require('Type')]}>
            <Select className='h-36 select-single' options={renderOpts(eventTypes)} />
        </Form.Item>
        <Form.Item
            name='parallel'
            valuePropName='checked'
            extra={<div className='text-comment line-h-18 italic'>
                Select this checkbox means to allow planning another operation in the same time with this event.
            </div>}
        >
            <Checkbox>Parallel event</Checkbox>
        </Form.Item>
        <Form.Item label="Location" name='pois'>
            <Location mode="multiple" />
        </Form.Item>
        <Form.Item
            label="Start time - Finish time" name="time"
            dependencies={['parallel']}
            rules={[Rule.Requires('Start time and Finish time'), { validator: validateTime }]}
        >
            <RangePicker {...{
                ...propContainer,
                className: 'w-full h-36',
                allowClear: false,
                showTime: { format: 'HH:mm', minuteStep: 15 },
                disabledDate: isPastDateDisabled ? disabledDate : undefined,
                format: SHORT_DATETIME_FORMAT,
                placeholder: ['Start time', 'Finish time'],
                onChange: handleChangeTime,
            }} />
        </Form.Item>

        <Form.Item label="Duration" name="duration">
            <Input className='h-36' suffix="Hour(s)" onChange={handleChangeDuration} />
        </Form.Item>
        <Form.Item label="Note" name="note">
            <TextArea rows={3} />
        </Form.Item>
        <div className='flex justify-end mb-3'>
            <AddChecklist
                data={operation}
                onChange={value => set({ operation: { ...operation, checklist_ids: value } })}
            />
        </div>
        <Button
            loading={loadingSubmit}
            className={['bt-primary', styles.btSubmit].join(' ')}
            data-full='true'
            htmlType='submit'
        >
            Save
        </Button>
    </Form >;
};

export default Event;
